import React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import axiosClient from '@/utils/client';
import {
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  MoreHoriz,
  DriveFileMove as MoveIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,

  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import { File as FileIcon, FileImage, FilePdf } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { FolderPlus } from '@untitled-ui/icons-react';

import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router';

import type { CommunityFile } from '@/types/community';
import type { File } from '@/types/file';
import type { Folder } from '@/types/folder';
import { paths } from '@/paths';
import ViewDocumentDialog from '@/pages/dashboard/dialogs/ViewDocument';
import { useDialog } from '@/hooks/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { Seo } from '@/components/seo';
import { CommunityPageHeader } from '@/components/common/community-page-header';

import { CreateFolderDialog } from '../components/create-folder-modal';
import DocumentBreadcrumbs from '../components/document-breadcrumbs';
import { EditFolderDialog } from '../components/edit-folder-modal';
import { EditFileDialog } from '../components/edit-file-modal';
import { UploadFileDialog } from '../components/upload-file-modal';

interface MenuState {
  anchorEl: HTMLElement | null;
  folder: Folder | null;
}

export function DocumentPage(): React.JSX.Element {
  // ALL hooks must be called first, before any conditional logic
  const { user } = useUser();
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [selectedDocument, setSelectedDocument] = React.useState<CommunityFile | null>(null);
  const [menuState, setMenuState] = React.useState<MenuState>({
    anchorEl: null,
    folder: null,
  });
  const viewDocumentDialog = useDialog();
  const folderDialog = useDialog();
  const fileDialog = useDialog();
  const editFolderDialog = useDialog();
  const editFileDialog = useDialog();

  const { data, refetch, error, isLoading } = useQuery({
    queryKey: ['folders', folderId],
    queryFn: async () => {
      try {
        const response = await axiosClient.get(`/api/v1/folders/${folderId}`);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          throw new Error('Folder not found');
        }
        throw error;
      }
    },
    retry: false,
  });

  const folders = data?.folders || [];
  const files = data?.files || [];
  const allItems = [...folders, ...files];

  console.log('📁 Document subfolder data:', data);
  console.log('📁 Folders:', folders);
  console.log('📄 Files:', files);
  console.log('📁 All items:', allItems);

  // Define isFolder function early so it can be used in handleMenuOpen
  const isFolder = (item: any) => item.type === 'folder';

  const handleFolderCreate = async () => {
    await refetch();
    folderDialog.handleClose();
  };

  const handleFileCreate = async () => {
    await refetch();
    fileDialog.handleClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    event.stopPropagation();
    console.log('🔍 handleMenuOpen called with item:', item);
    console.log('🔍 Item type:', item.type);
    console.log('🔍 isFolder(item):', isFolder(item));
    
    // Set the menu state first so the dialogs have the correct item
    setMenuState({ anchorEl: null, folder: item });
    
    // Then open the appropriate dialog based on item type
    if (isFolder(item)) {
      console.log('🔍 Opening editFolderDialog');
      editFolderDialog.handleOpen();
    } else {
      console.log('🔍 Opening editFileDialog');
      editFileDialog.handleOpen();
    }
  };

  const handleMenuClose = () => {
    setMenuState({
      anchorEl: null,
      folder: null,
    });
    editFolderDialog.handleClose();
  };



  const handleMove = () => {
    // Implement move functionality
    handleMenuClose();
  };

  const handleEdit = React.useCallback(async () => {
    handleMenuClose();
    // Implement edit functionality
    await refetch();
  }, [refetch]);


  const renderFileTypeIcon = (item: File) => {
    const mimeType = item.mime_type;

    switch (mimeType) {
      case 'pdf':
        return <FilePdf />;
      case 'jpeg':
        return <FileImage />;
      default:
        return <FileIcon />;
    }
  };

  const renderDocumentIcon = (item: any) => {
    const isPrivate = (item).visibility === 'private';
    const folder = isFolder(item);

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Avatar>{folder ? <FolderIcon /> : renderFileTypeIcon(item as unknown as File)}</Avatar>
        {isPrivate ? <Box
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (theme) => theme.shadows[1],
            }}
          >
            <LockIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          </Box> : null}
      </Box>
    );
  };

  const renderDocumentOrFile = (item: any) => {
    const isPrivate = (item).visibility === 'private';
    return (
      <ListItemButton
        onClick={() => {
          if (!isFolder(item)) {
            setSelectedDocument(item as unknown as CommunityFile);
            viewDocumentDialog.handleOpen();
            console.log('Viewing document:', item);
            return;
          }
          navigate(paths.documents.folder(item.id));
        }}
      >
        <ListItemAvatar>{renderDocumentIcon(item)}</ListItemAvatar>
        <ListItemText
          primary={item.name}
          secondary={isFolder(item) && (item)?.item_count ? `${(item).item_count} items` : ''}
        />
        <RoleGuard
          requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER, ACCOUNT_ROLES.ACCOUNT_OWNER]}
          requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
        >
          <ListItemSecondaryAction>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                if (isFolder(item)) {
                  handleMenuOpen(event, item);
                } else {
                  // For files, set menu state and open edit file dialog
                  setMenuState({ anchorEl: null, folder: item as unknown as Folder });
                  editFileDialog.handleOpen();
                }
              }}
              size="small"
            >
              <MoreHoriz />
            </IconButton>
          </ListItemSecondaryAction>
        </RoleGuard>
      </ListItemButton>
    );
  };

  // Show folder not found error
  if (!isLoading && error?.message === 'Folder not found') {
    return (
      <>
        <Seo title="Folder Not Found" />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 3,
            backgroundColor: 'background.default',
            minHeight: '100vh',
          }}
        >
          <Container maxWidth="xl">
            <CommunityPageHeader
              breadcrumbs={[
                {
                  label: 'Home',
                  href: '/',
                },
                {
                  label: 'Documents',
                  href: paths.documents.index,
                },
                {
                  label: 'Folder Not Found',
                },
              ]}
              icon={<FileIcon size={36} />}
              subtitle="The folder you're looking for doesn't exist or has been removed"
              title="Folder Not Found"
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: '200px',
                  height: '200px',
                  color: 'primary.main',
                  opacity: 0.8,
                  mb: 4,
                }}
              >
                <FileIcon size={200} />
              </Box>

              <Typography color="text.secondary" variant="body1" sx={{ mb: 2 }}>
                Folder ID: <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>{folderId}</code>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => window.history.back()}
                  sx={{ minWidth: 120 }}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  href={paths.documents.index}
                  sx={{ minWidth: 120 }}
                >
                  View All Documents
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title="Documents" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 8,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={3}>
            {user?.selected_community_id !== null ? (
              <Card>
                <CardHeader
                  action={
                    <Box gap={2} sx={{ display: 'flex', alignItems: 'center' }}>
                      <RoleGuard
                        requiredAccountRoles={[
                          ACCOUNT_ROLES.ACCOUNT_ADMIN,
                          ACCOUNT_ROLES.ACCOUNT_MANAGER,
                          ACCOUNT_ROLES.ACCOUNT_OWNER,
                        ]}
                        requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
                      >
                        <Button onClick={folderDialog.handleOpen} startIcon={<FolderPlus />} variant="contained">
                          Create Folder
                        </Button>
                        <Button
                          onClick={fileDialog.handleOpen}
                          startIcon={<CloudUploadOutlinedIcon />}
                          variant="contained"
                      >
                          Add Document
                        </Button>
                      </RoleGuard>
                    </Box>
                  }
                  avatar={
                    <Avatar>
                      <FolderIcon />
                    </Avatar>
                  }
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    '& .MuiCardHeader-content': {
                      flex: 1,
                    },
                    '& .MuiCardHeader-action': {
                      margin: 0,
                      alignSelf: 'center',
                    },
                  }}
                  title={
                    isLoading ? 'Loading...' : <DocumentBreadcrumbs ancestors={data?.ancestors} parent={data?.parent} />
                  }
                />
                <CardContent>
                  {isLoading ? (
                    <Typography variant="body1">Loading...</Typography>
                  ) : allItems.length === 0 ? (
                    <Typography variant="body1">No folders or files found</Typography>
                  ) : (
                    <List>
                      {allItems.map((item: any, index: number) => (
                        <React.Fragment key={item.id || index}>
                          <ListItem disablePadding>{renderDocumentOrFile(item)}</ListItem>
                          {index !== allItems.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Alert severity="info">
                  <AlertTitle>Document Info</AlertTitle>
                  To view documents, you must be viewing as a single community.
                </Alert>
              </Box>
            )}
          </Stack>
        </Container>
      </Box>



      <CreateFolderDialog
        folderId={folderId}
        onClose={folderDialog.handleClose}
        onCreate={handleFolderCreate}
        open={folderDialog.open}
      />
      <UploadFileDialog
        folderId={folderId}
        onClose={fileDialog.handleClose}
        onCreate={handleFileCreate}
        open={fileDialog.open}
      />
      <EditFolderDialog
        folder={menuState.folder}
        onClose={() => {
          editFolderDialog.handleClose();
          setMenuState({ anchorEl: null, folder: null });
        }}
        onEdit={handleEdit}
        onSuccess={async () => {
          await refetch();
          editFolderDialog.handleClose();
          setMenuState({ anchorEl: null, folder: null });
        }}
        open={editFolderDialog.open}
      />
      <EditFileDialog
        file={menuState.folder as unknown as File}
        onClose={() => {
          editFileDialog.handleClose();
          setMenuState({ anchorEl: null, folder: null });
        }}
        onEdit={handleEdit}
        onSuccess={async () => {
          await refetch();
          editFileDialog.handleClose();
          setMenuState({ anchorEl: null, folder: null });
        }}
        open={editFileDialog.open}
      />
      <ViewDocumentDialog
        file={selectedDocument}
        onClose={() => {
          setSelectedDocument(null);
          navigate(location.pathname);
          viewDocumentDialog.handleClose();
        }}
        open={viewDocumentDialog.open}
      />
    </>
  );
}
